/**
 * <voice-clip src="..."> — on-brand audio button.
 *
 * Replaces the native <audio controls> element for hero / sample clips.
 * Renders a gold play / pause button + caption + an audio-reactive
 * 6-bar mini-waveform. State driven by attributes (idle / loading /
 * playing / paused / errored). No Elm port needed — the element is
 * fully self-contained.
 *
 * Optional `probe` attribute: preloads metadata on mount so a missing
 * source (e.g. a clip not yet uploaded to the audio bucket) presents
 * as a quiet "coming soon" state up front instead of tap-then-fail.
 * A tap while errored retries the load, so the element heals itself
 * once the asset exists — no redeploy, no code change.
 */

type State = "idle" | "loading" | "playing" | "paused" | "errored";

const PLAY_PATH =
  "M8 5v14l11-7z";  // standard play triangle
const PAUSE_PATH =
  "M6 4h4v16H6V4zm8 0h4v16h-4V4z";

class VoiceClip extends HTMLElement {
  #audio: HTMLAudioElement | null = null;
  #button: HTMLButtonElement | null = null;
  #icon: SVGPathElement | null = null;
  #caption: HTMLElement | null = null;
  #bars: ReadonlyArray<HTMLElement> = [];
  #state: State = "idle";
  #raf: number | null = null;

  static get observedAttributes(): readonly string[] {
    return ["src"];
  }

  connectedCallback(): void {
    if (this.#button !== null) return;
    this.#render();
    this.#attach();
  }

  disconnectedCallback(): void {
    this.#stopBars();
    this.#audio?.pause();
  }

  attributeChangedCallback(name: string): void {
    if (name === "src" && this.#audio !== null) {
      this.#audio.src = this.getAttribute("src") ?? "";
    }
  }

  #render(): void {
    const src = this.getAttribute("src") ?? "";
    const caption = this.getAttribute("caption") ?? "Hear Lisa · 30-second welcome";
    const ariaLabel = this.getAttribute("aria-label") ?? `Play ${caption}`;

    // Audio element — hidden, controlled programmatically.
    // No crossOrigin: the bars are synthetic (no Web Audio analysis),
    // and a CORS-mode fetch would refuse to play from an audio host
    // that doesn't send ACAO headers. Plain media fetches always work.
    const audio = document.createElement("audio");
    audio.src = src;
    audio.preload = this.hasAttribute("probe") ? "metadata" : "none";
    this.appendChild(audio);
    this.#audio = audio;

    // Wrapper
    const wrap = document.createElement("div");
    wrap.className =
      "voice-clip flex items-center gap-3 sm:gap-4 px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-full";
    this.appendChild(wrap);

    // Play / pause button
    const button = document.createElement("button");
    button.type = "button";
    button.className = "voice-clip__btn tap juice-tap juice-magnet";
    button.setAttribute("aria-label", ariaLabel);

    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", "20");
    svg.setAttribute("height", "20");
    svg.setAttribute("aria-hidden", "true");
    const path = document.createElementNS(svgNS, "path");
    path.setAttribute("d", PLAY_PATH);
    path.setAttribute("fill", "currentColor");
    svg.appendChild(path);
    button.appendChild(svg);
    wrap.appendChild(button);
    this.#button = button;
    this.#icon = path;

    // Caption (label + state)
    const cap = document.createElement("div");
    cap.className = "voice-clip__caption flex flex-col text-left min-w-0";
    const label = document.createElement("span");
    label.className =
      "text-cream/85 text-[11px] sm:text-xs tracking-[0.25em] uppercase font-medium truncate";
    label.textContent = caption;
    const sub = document.createElement("span");
    sub.className =
      "voice-clip__state text-gold-soft/65 text-[9px] sm:text-[10px] tracking-[0.3em] uppercase truncate";
    sub.textContent = "Tap to play";
    cap.appendChild(label);
    cap.appendChild(sub);
    wrap.appendChild(cap);
    this.#caption = sub;

    // Audio-reactive bars (6, animated when playing)
    const bars = document.createElement("div");
    bars.className =
      "voice-clip__bars hidden sm:flex items-end gap-[3px] ml-auto h-5 shrink-0";
    const barEls: HTMLElement[] = [];
    for (let i = 0; i < 6; i += 1) {
      const b = document.createElement("span");
      b.className = "voice-clip__bar";
      b.style.setProperty("--i", String(i));
      bars.appendChild(b);
      barEls.push(b);
    }
    wrap.appendChild(bars);
    this.#bars = barEls;
  }

  #attach(): void {
    const audio = this.#audio;
    const button = this.#button;
    if (!audio || !button) return;

    button.addEventListener("click", () => {
      if (this.#state === "playing") {
        audio.pause();
      } else {
        if (this.#state === "errored") {
          // Retry from scratch — the asset may have appeared since the
          // last attempt (e.g. uploaded to the bucket after page load).
          audio.load();
        }
        this.#setState("loading");
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.then === "function") {
          playPromise.catch(() => this.#setState("errored"));
        }
      }
    });

    audio.addEventListener("playing", () => this.#setState("playing"));
    audio.addEventListener("pause", () => {
      if (audio.ended) return;
      this.#setState("paused");
    });
    audio.addEventListener("ended", () => this.#setState("idle"));
    audio.addEventListener("waiting", () => this.#setState("loading"));
    audio.addEventListener("error", () => this.#setState("errored"));
  }

  #setState(s: State): void {
    if (this.#state === s) return;
    this.#state = s;
    this.dataset["state"] = s;
    if (this.#icon !== null) {
      this.#icon.setAttribute("d", s === "playing" ? PAUSE_PATH : PLAY_PATH);
    }
    // Errored reads as "not available yet", not a control — take it out
    // of the interactive contract for assistive tech (the tap remains
    // wired as a silent retry).
    this.#button?.setAttribute("aria-disabled", s === "errored" ? "true" : "false");
    if (this.#caption !== null) {
      this.#caption.textContent =
        s === "playing" ? "Playing" :
        s === "paused" ? "Paused" :
        s === "loading" ? "Loading…" :
        s === "errored" ? "Audio sample coming soon" :
        "Tap to play";
    }
    if (s === "playing") this.#startBars();
    else this.#stopBars();
  }

  #startBars(): void {
    if (this.#raf !== null) return;
    const start = performance.now();
    const tick = (now: number): void => {
      const t = (now - start) / 1000;
      for (let i = 0; i < this.#bars.length; i += 1) {
        const bar = this.#bars[i];
        if (!bar) continue;
        // Pseudo-random per-bar wave: stagger phase, sin × abs, jitter via
        // a second slower wave to avoid robotic uniformity.
        const phase = i * 0.7;
        const a = Math.abs(Math.sin(t * 4 + phase));
        const b = Math.abs(Math.sin(t * 1.3 + phase * 0.5));
        const h = 18 + (a * 0.7 + b * 0.3) * 78;  // 18-96% height
        bar.style.height = `${h}%`;
      }
      this.#raf = requestAnimationFrame(tick);
    };
    this.#raf = requestAnimationFrame(tick);
  }

  #stopBars(): void {
    if (this.#raf !== null) {
      cancelAnimationFrame(this.#raf);
      this.#raf = null;
    }
    for (const bar of this.#bars) {
      bar.style.height = "20%";
    }
  }
}

export const registerVoiceClip = (): void => {
  if (customElements.get("voice-clip") !== undefined) return;
  customElements.define("voice-clip", VoiceClip);
};
